import React, { useState, useEffect } from "react";
import {
  X, MapPin, Navigation, Camera, Loader2, Upload,
  CheckCircle, Search, Clock, CreditCard, Video,
  Building, Users, Utensils, Wifi, Car, Cigarette,
  Shield, Award, FileText, UserCheck, FileCheck,
  Banknote, Phone, Mail, Building2, User,
  Palette, Image, Check, Plus,
} from "lucide-react";
import toast from "react-hot-toast";
import { ServiceFormHeader, ServiceFormFooter } from "./ServiceFormShell";
import api from "../../utils/axios";
import CustomTextEditor from "../UI/CustomTextEditor";
import VendorLiftAI from "./VendorLiftAI";
import ServiceAIAssistant from "./ServiceAIAssistant";
import AIPackageBuilder from "./AIPackageBuilder";
import AIKeywordFill from "./AIKeywordFill";
import { includeGroups } from "../../data/packageTaxonomy";

const ServiceModal = ({ service, isOpen, onClose, onSave, inline = false }) => {
  const [formData, setFormData] = useState({
    name: "",
    serviceType: "venue",
    subCategories: [],
    description: "",
    paymentMethods: [],
    images: [],
    videos: [],
    amenityImages: [],
    eventShowcase: [],
    serviceLocation: { type: "Point", coordinates: [] },
    serviceAddress: {
      street: "", area: "", city: "", state: "",
      country: "India", pincode: "", landmark: "", fullAddress: "",
    },
    venueSlots: [
      { slot: "morning", available: false },
      { slot: "afternoon", available: false },
      { slot: "evening", available: false },
      { slot: "full-day", available: false },
    ],
    areas_available: [],
    venueType: "mixed",
    cuisineAllowed: { veg: false, nonVeg: false },
    cateringMenu: [],
    pricing: {
      model: "per_pax",
      perPax: { veg: 0, nonVeg: 0 },
      fixedBooking: { basePrice: 0, description: "" },
      packagePerDay: { price: 0, noOfRooms: 0, noOfPax: 0, noOfMeals: 0 },
    },
    amenities: [],
    parking: { available: false, capacity: 0 },
    smokingAllowed: false,
    externalVendors: {
      catering: "in_house_only",
      decor: "in_house_only",
      dj: "in_house_only",
      liquor: "not_allowed",
    },
    accessibilityFeatures: [],
    nearbyLandmarks: [],
    businessDocuments: {
      businessLicense: null, taxRegistration: null, gstCertificate: null,
      identityProof: null, bankProof: null, fssaiCertificate: null, liquorLicense: null,
    },
    payoutBankDetails: {
      accountHolderName: "", accountNumber: "", ifscCode: "", bankName: "", branchName: "",
    },
    contact_details: { phone: "", alternative_phone: "", email: "" },
    logo: null,
    isLive: true,
    isGst: false,
  });

  const [loading, setLoading] = useState(false);
  const [uploadingImages, setUploadingImages] = useState(false);
  const [uploadingImagesLogo, setUploadingImagesLogo] = useState(false);
  const [uploadingBusinessDocs, setUploadingBusinessDocs] = useState(false);
  const [uploadingAmenityImages, setUploadingAmenityImages] = useState(false);
  const [uploadingEventImages, setUploadingEventImages] = useState(false);
  const [locationSearch, setLocationSearch] = useState("");
  const [locationSuggestions, setLocationSuggestions] = useState([]);
  const [searchingLocation, setSearchingLocation] = useState(false);
  const [activeTab, setActiveTab] = useState("basic");

  // ── Populate form when editing ───────────────────────────────────────────────
  useEffect(() => {
    if (service) {
      setFormData({
        name: service.name || "",
        serviceType: service.serviceType || "venue",
        subCategories: service.subCategories || [],
        description: service.description || "",
        venueType: service.venueType || "mixed",
        isGst: service.isGst ?? false,
        paymentMethods: service.paymentMethods || [],
        images: service.images || [],
        videos: service.videos || [],
        amenityImages: service.amenityImages || [],
        eventShowcase: service.eventShowcase || [],
        serviceLocation: service.serviceLocation || { type: "Point", coordinates: [] },
        serviceAddress: service.serviceAddress || {
          street: "", area: "", city: "", state: "",
          country: "India", pincode: "", landmark: "", fullAddress: "",
        },
        venueSlots: service.venueSlots || [
          { slot: "morning", available: false },
          { slot: "afternoon", available: false },
          { slot: "evening", available: false },
          { slot: "full-day", available: false },
        ],
        areas_available: service.areas_available || [],
        cuisineAllowed: service.cuisineAllowed || { veg: false, nonVeg: false },
        cateringMenu: service.cateringMenu || [],
        pricing: service.pricing || {
          model: "per_pax",
          perPax: { veg: 0, nonVeg: 0 },
          fixedBooking: { basePrice: null, description: "" },
          packagePerDay: { price: 0, noOfRooms: 0, noOfPax: 0, noOfMeals: 0 },
        },
        amenities: service.amenities || [],
        parking: service.parking || { available: false, capacity: 0 },
        smokingAllowed: service.smokingAllowed || false,
        externalVendors: service.externalVendors || {
          catering: "in_house_only", decor: "in_house_only",
          dj: "in_house_only", liquor: "not_allowed",
        },
        accessibilityFeatures: service.accessibilityFeatures || [],
        nearbyLandmarks: service.nearbyLandmarks || [],
        isLive: service.isLive !== undefined ? service.isLive : true,
        businessDocuments: service.businessDocuments || {
          businessLicense: null, taxRegistration: null, gstCertificate: null,
          identityProof: null, bankProof: null, fssaiCertificate: null, liquorLicense: null,
        },
        payoutBankDetails: service.payoutBankDetails || {
          accountHolderName: "", accountNumber: "", ifscCode: "", bankName: "", branchName: "",
        },
        logo: service.logo || null,
        contact_details: {
          phone: service.contact_details?.phone || "",
          alternative_phone: service.contact_details?.alternative_phone || "",
          email: service.contact_details?.email || "",
        },
      });
    } else {
      // Reset to defaults for new service
      setFormData((prev) => ({ ...prev, serviceType: "venue" }));
    }
    // Always reset to basic tab on open
    setActiveTab("basic");
  }, [service, isOpen]);

  // ── Location search debounce ─────────────────────────────────────────────────
  useEffect(() => {
    const delayedSearch = setTimeout(() => {
      if (locationSearch.length >= 2) searchLocations();
      else setLocationSuggestions([]);
    }, 300);
    return () => clearTimeout(delayedSearch);
  }, [locationSearch]);

  const searchLocations = async () => {
    try {
      setSearchingLocation(true);
      const response = await api.get(`/users/places/autocomplete?input=${encodeURIComponent(locationSearch)}`);
      if (response.data.success) setLocationSuggestions(response.data.data);
    } catch (error) {
      console.error("Location search failed:", error);
    } finally {
      setSearchingLocation(false);
    }
  };

  const selectLocation = async (suggestion) => {
    try {
      const response = await api.get(`/users/address?lat=${suggestion.latitude}&lng=${suggestion.longitude}`);
      if (response.data.success) {
        const addressData = response.data.data;
        setFormData({
          ...formData,
          serviceLocation: { type: "Point", coordinates: [suggestion.longitude, suggestion.latitude] },
          serviceAddress: {
            ...formData.serviceAddress,
            city: addressData.city || suggestion.main_text,
            state: addressData.state || suggestion.secondary_text?.split(",")[0],
            pincode: addressData.pincode || formData.serviceAddress.pincode,
            fullAddress: suggestion.description,
            ...addressData,
          },
        });
        setLocationSearch("");
        setLocationSuggestions([]);
        toast.success("Location selected");
      }
    } catch (error) {
      setFormData({
        ...formData,
        serviceLocation: { type: "Point", coordinates: [suggestion.longitude, suggestion.latitude] },
        serviceAddress: {
          ...formData.serviceAddress,
          city: suggestion.main_text,
          state: suggestion.secondary_text?.split(",")[0],
          fullAddress: suggestion.description,
          country: "India",
        },
      });
      setLocationSearch("");
      setLocationSuggestions([]);
      toast.success("Location selected");
    }
  };

  const getCurrentLocation = async () => {
    try {
      if (!navigator.geolocation) { toast.error("Geolocation is not supported"); return; }
      toast.loading("Getting your location...");
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const { latitude, longitude } = position.coords;
          try {
            const response = await api.get(`/users/address?lat=${latitude}&lng=${longitude}`);
            if (response.data.success) {
              setFormData({
                ...formData,
                serviceLocation: { type: "Point", coordinates: [longitude, latitude] },
                serviceAddress: { ...formData.serviceAddress, ...response.data.data },
              });
              toast.dismiss();
              toast.success("Current location set");
            }
          } catch (error) {
            toast.dismiss();
            toast.error("Failed to get address details");
          }
        },
        () => { toast.dismiss(); toast.error("Failed to get your location"); }
      );
    } catch (error) {
      toast.error("Failed to get location");
    }
  };

  const handleImageUpload = async (files, type = "images") => {
    try {
      const loadingState = {
        images: setUploadingImages,
        amenityImages: setUploadingAmenityImages,
        eventShowcase: setUploadingEventImages,
      };
      loadingState[type](true);
      const uploadFormData = new FormData();
      Array.from(files).forEach((file) => uploadFormData.append("files", file));
      const response = await api.post("/image/upload", uploadFormData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (response.data.success) {
        const newImages = response.data.data.files.map((file) => ({ url: file.url, caption: file.name }));
        setFormData({ ...formData, [type]: [...formData[type], ...newImages] });
        toast.success("Images uploaded successfully");
      }
    } catch (error) {
      toast.error("Failed to upload images");
    } finally {
      const loadingState = {
        images: setUploadingImages,
        amenityImages: setUploadingAmenityImages,
        eventShowcase: setUploadingEventImages,
      };
      loadingState[type](false);
    }
  };

  const handleImageUploadLogo = async (files) => {
    try {
      setUploadingImagesLogo(true);
      const uploadFormData = new FormData();
      Array.from(files).forEach((file) => uploadFormData.append("files", file));
      const response = await api.post("/image/upload", uploadFormData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (response.data.success) {
        const newImages = response.data.data.files.map((file) => ({ url: file.url, caption: file.name }));
        setFormData({ ...formData, logo: newImages[0].url });
        toast.success("Logo uploaded successfully");
      }
    } catch (error) {
      toast.error("Failed to upload logo");
    } finally {
      setUploadingImagesLogo(false);
    }
  };

  const handleBusinessDocUpload = async (files, docType) => {
    try {
      setUploadingBusinessDocs(true);
      const uploadFormData = new FormData();
      Array.from(files).forEach((file) => uploadFormData.append("files", file));
      const response = await api.post("/image/upload", uploadFormData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (response.data.success) {
        const newDoc = { url: response.data.data.files[0].url, name: response.data.data.files[0].name };
        setFormData({
          ...formData,
          businessDocuments: { ...formData.businessDocuments, [docType]: newDoc },
        });
        toast.success(`${docType.replace(/([A-Z])/g, " $1").toUpperCase()} uploaded successfully`);
      }
    } catch (error) {
      toast.error("Failed to upload document");
    } finally {
      setUploadingBusinessDocs(false);
    }
  };

  // ── Areas available helpers ──────────────────────────────────────────────────
  const addArea = () => {
    setFormData({
      ...formData,
      areas_available: [
        ...(formData.areas_available || []),
        { title: "", type: "", seating: 0, floating: 0, description: "" },
      ],
    });
  };

  const removeArea = (idx) => {
    setFormData({
      ...formData,
      areas_available: formData.areas_available.filter((_, i) => i !== idx),
    });
    toast.success("Area removed");
  };

  const updateArea = (idx, field, value) => {
    const newAreas = [...formData.areas_available];
    newAreas[idx] = { ...newAreas[idx], [field]: value };
    setFormData({ ...formData, areas_available: newAreas });
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      if (!formData.name) { toast.error("Venue name is required"); setLoading(false); return; }
      if (!formData.serviceLocation?.coordinates || formData.serviceLocation.coordinates.length !== 2) {
        toast.error("Please select a service location"); setLoading(false); return;
      }
      if (!formData.serviceAddress?.city || !formData.serviceAddress?.state || !formData.serviceAddress?.pincode) {
        toast.error("Complete address is required"); setLoading(false); return;
      }
      let response;
      if (service && service._id) {
        response = await api.put(`/api/v1/services/${service._id}`, formData);
      } else {
        response = await api.post("/api/v1/services/", formData);
      }
      if (response.data.success) {
        toast.success(service ? "Service updated successfully" : "Service created successfully");
        onSave();
        onClose();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to save service");
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const subCategoryOptions = [
    "convention_hall", "banquet_hall", "lawn_garden", "hotel", "farmhouse",
    "beach_resort", "marriage_hall", "3_star_hotel", "4_star_hotel", "5_star_hotel",
    "party_hall", "wedding_venue", "reception_hall", "function_hall", "ballroom",
    "rooftop_venue", "outdoor_pavilion", "garden_venue", "poolside_venue", "resort",
  ];

  const amenitiesOptions = [
    "parking", "ac", "wifi", "projector", "stage", "sound_system", "valet_service",
    "bridal_room", "generator_backup", "catering_kitchen", "bar_counter", "dance_floor",
    "green_room", "security_service", "photography_setup", "decoration_service",
    "elevator_access", "wheelchair_accessible", "outdoor_space", "garden_area",
    "swimming_pool", "gym_facility", "spa_services",
  ];

  const accessibilityOptions = [
    "wheelchair_accessible", "elevator", "ramp", "disabled_parking", "accessible_restroom",
  ];

  const VENUE_TABS = ["basic", "location", "media", "details", "pricing", "packages", "documents", "payout", "policies"];

  return (
    <div className={inline ? "w-full" : "fixed inset-0 z-50 overflow-y-auto"}>
      {!inline && (
        <div className="flex items-center justify-center min-h-screen px-4">
          <div className="fixed inset-0 bg-black bg-opacity-50" onClick={onClose} />
        </div>
      )}
      <div className={inline ? "" : "relative bg-white rounded-2xl shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-y-auto mx-auto"}>

          <ServiceFormHeader
            icon={Building2}
            title={service ? "Edit Venue" : "Add New Venue"}
            subtitle="Venue / Banquet Hall"
            tabs={VENUE_TABS}
            activeTab={activeTab}
            onTabChange={setActiveTab}
            onClose={onClose}
            inline={inline}
          />

          <div className="px-5 sm:px-7 py-6 space-y-6">

            {/* ── BASIC ───────────────────────────────────────────────── */}
            {activeTab === "basic" && (
              <>
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-[13px] font-semibold text-gray-700">Venue Name *</label>
                    <ServiceAIAssistant
                      serviceType={formData.serviceType || "venue"}
                      step="name"
                      formData={formData}
                      onApply={({ name }) => setFormData({ ...formData, name })}
                    />
                  </div>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-4 py-2.5 text-sm text-gray-900 placeholder:text-gray-400 border border-gray-200 rounded-xl hover:border-gray-300 focus:outline-none focus:ring-4 focus:ring-purple-500/10 focus:border-purple-400 transition-all"
                    placeholder="Grand Palace Banquet Hall"
                  />
                </div>

                <div>
                  <label className="block text-[13px] font-semibold text-gray-700 mb-1.5">Venue Type</label>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2 max-h-60 overflow-y-auto border border-gray-300 rounded-lg p-3">
                    {subCategoryOptions.map((subCat) => (
                      <label key={subCat} className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={formData.subCategories?.includes(subCat) || false}
                          onChange={() => setFormData({ ...formData, subCategories: [subCat] })}
                          className="w-4 h-4  border-gray-300 rounded focus:ring-purple-500 accent-purple-600 cursor-pointer"
                        />
                        <span className="text-sm text-gray-700">
                          {subCat.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())}
                        </span>
                      </label>
                    ))}
                  </div>
                  {formData.subCategories?.length > 0 && (
                    <p className="text-xs font-medium text-gray-500 mt-1.5">{formData.subCategories.length} selected</p>
                  )}
                </div>

                <div>
                  <label className="block text-[13px] font-semibold text-gray-700 mb-1.5">Description</label>
                  <div className="mb-3">
                    <VendorLiftAI
                      serviceType={formData.serviceType || "venue"}
                      formData={formData}
                      setFormData={setFormData}
                    />
                  </div>
                  <CustomTextEditor
                    value={formData.description}
                    onChange={(html) => setFormData({ ...formData, description: html })}
                  />
                </div>

                <div>
                  <label className="block text-[13px] font-semibold text-gray-700 mb-1.5">
                    <CreditCard className="inline w-4 h-4 mr-1" />
                    Accepted Payment Methods
                  </label>
                  <div className="flex space-x-4">
                    {["cash", "card", "upi"].map((method) => (
                      <label key={method} className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={formData.paymentMethods?.includes(method) || false}
                          onChange={(e) => {
                            const newMethods = e.target.checked
                              ? [...(formData.paymentMethods || []), method]
                              : (formData.paymentMethods || []).filter((m) => m !== method);
                            setFormData({ ...formData, paymentMethods: newMethods });
                          }}
                          className="w-4 h-4  border-gray-300 rounded focus:ring-purple-500 accent-purple-600 cursor-pointer"
                        />
                        <span className="text-sm text-gray-700 capitalize">{method}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Contact Information */}
                <div className="rounded-2xl border border-gray-200/70 bg-gray-50/60 p-5 space-y-4">
                  <div className="flex items-center space-x-3 pb-2">
                    <div className="w-10 h-10 bg-gradient-to-r from-purple-100 to-pink-100 rounded-xl flex items-center justify-center">
                      <Phone className="w-5 h-5 text-purple-600" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-gray-900">Contact Information</h3>
                      <p className="text-xs text-gray-500">How guests can reach you</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Primary Contact *</label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                          <Phone className="w-4 h-4 text-gray-400" />
                        </div>
                        <input
                          type="tel"
                          value={formData.contact_details?.phone || ""}
                          onChange={(e) => setFormData({ ...formData, contact_details: { ...formData.contact_details, phone: e.target.value } })}
                          className="w-full pl-11 pr-4 py-2.5 text-sm text-gray-900 placeholder:text-gray-400 border border-gray-200 rounded-xl hover:border-gray-300 focus:outline-none focus:ring-4 focus:ring-purple-500/10 focus:border-purple-400 transition-all"
                          placeholder="+91 98765 43210"
                          maxLength={15}
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Alternative Contact <span className="text-gray-400 text-xs">(Optional)</span>
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                          <Phone className="w-4 h-4 text-gray-400" />
                        </div>
                        <input
                          type="tel"
                          value={formData.contact_details?.alternative_phone || ""}
                          onChange={(e) => setFormData({ ...formData, contact_details: { ...formData.contact_details, alternative_phone: e.target.value } })}
                          className="w-full pl-11 pr-4 py-2.5 text-sm text-gray-900 placeholder:text-gray-400 border border-gray-200 rounded-xl hover:border-gray-300 focus:outline-none focus:ring-4 focus:ring-purple-500/10 focus:border-purple-400 transition-all"
                          placeholder="+91 98765 43211"
                          maxLength={15}
                        />
                      </div>
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Email Address *</label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                          <Mail className="w-4 h-4 text-gray-400" />
                        </div>
                        <input
                          type="email"
                          value={formData.contact_details?.email || ""}
                          onChange={(e) => setFormData({ ...formData, contact_details: { ...formData.contact_details, email: e.target.value } })}
                          className="w-full pl-11 pr-4 py-2.5 text-sm text-gray-900 placeholder:text-gray-400 border border-gray-200 rounded-xl hover:border-gray-300 focus:outline-none focus:ring-4 focus:ring-purple-500/10 focus:border-purple-400 transition-all"
                          placeholder="venue@example.com"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}

            {/* ── LOCATION ──────────────────────────────────────────────── */}
            {activeTab === "location" && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="font-['Manrope'] text-base font-bold text-gray-900">Service Location *</h3>
                  <button
                    onClick={getCurrentLocation}
                    className="inline-flex items-center gap-2 h-10 px-4 rounded-xl text-sm font-semibold bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-md shadow-purple-600/20 hover:shadow-lg transition-alltransition-colors"
                  >
                    <Navigation className="w-4 h-4" />
                    <span>Current Location</span>
                  </button>
                </div>
                <div className="relative">
                  <Search className="absolute left-3 top-3.5 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    value={locationSearch}
                    onChange={(e) => setLocationSearch(e.target.value)}
                    placeholder="Search for location..."
                    className="w-full pl-10 pr-4 py-2.5 text-sm text-gray-900 placeholder:text-gray-400 border border-gray-200 rounded-xl hover:border-gray-300 focus:outline-none focus:ring-4 focus:ring-purple-500/10 focus:border-purple-400 transition-all"
                  />
                  {searchingLocation && <Loader2 className="absolute right-3 top-3.5 w-5 h-5 animate-spin text-purple-600" />}
                  {locationSuggestions.length > 0 && (
                    <div className="absolute z-20 w-full bg-white border border-gray-200 rounded-xl shadow-xl max-h-60 overflow-y-auto mt-1">
                      {locationSuggestions.map((suggestion, idx) => (
                        <button
                          key={idx}
                          onClick={() => selectLocation(suggestion)}
                          className="w-full text-left px-4 py-3 hover:bg-gray-50 border-b border-gray-100 transition-colors"
                        >
                          <div className="flex items-start space-x-3">
                            <MapPin className="w-5 h-5 text-gray-400 mt-0.5 flex-shrink-0" />
                            <div>
                              <div className="font-medium text-gray-900">{suggestion.main_text}</div>
                              <div className="text-sm text-gray-600">{suggestion.secondary_text}</div>
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                {formData.serviceAddress?.fullAddress && (
                  <div className="p-4 bg-purple-50/60 border border-purple-200 rounded-xl flex items-start space-x-3">
                    <CheckCircle className="w-5 h-5 text-purple-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-semibold text-gray-900">Location Selected</p>
                      <p className="text-sm text-purple-700 mt-1">{formData.serviceAddress.fullAddress}</p>
                    </div>
                  </div>
                )}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {[
                    { key: "street", label: "Street", ph: "Street address" },
                    { key: "area", label: "Area", ph: "Area/Locality" },
                    { key: "city", label: "City *", ph: "Hyderabad" },
                    { key: "state", label: "State *", ph: "Telangana" },
                    { key: "pincode", label: "PIN Code *", ph: "500001", max: 6 },
                    { key: "landmark", label: "Landmark", ph: "Near..." },
                  ].map((f) => (
                    <div key={f.key}>
                      <label className="block text-[13px] font-semibold text-gray-700 mb-1.5">{f.label}</label>
                      <input
                        type="text"
                        value={formData.serviceAddress?.[f.key] || ""}
                        onChange={(e) => setFormData({ ...formData, serviceAddress: { ...formData.serviceAddress, [f.key]: e.target.value } })}
                        className="w-full px-4 py-2.5 text-sm text-gray-900 placeholder:text-gray-400 border border-gray-200 rounded-xl hover:border-gray-300 focus:outline-none focus:ring-4 focus:ring-purple-500/10 focus:border-purple-400 transition-all"
                        placeholder={f.ph}
                        maxLength={f.max}
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* ── MEDIA ─────────────────────────────────────────────────── */}
            {activeTab === "media" && (
              <div className="space-y-6">
                <ServiceAIAssistant
                  serviceType={formData.serviceType || "venue"}
                  step="photos"
                  formData={formData}
                />
                {/* Logo */}
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-['Manrope'] text-base font-bold text-gray-900">Logo</h3>
                    <label className="inline-flex items-center gap-2 h-10 px-4 rounded-xl text-sm font-semibold bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-md shadow-purple-600/20 hover:shadow-lg transition-all cursor-pointer transition-colors">
                      {uploadingImagesLogo ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />}
                      <span>Upload Logo</span>
                      <input type="file" accept="image/*" onChange={(e) => handleImageUploadLogo(e.target.files)} className="hidden" />
                    </label>
                  </div>
                  {formData.logo && (
                    <div className="relative inline-block group">
                      <img src={formData.logo} alt="Logo" className="w-24 h-24 object-cover rounded-2xl border border-gray-200/70" />
                      <button
                        onClick={() => setFormData({ ...formData, logo: null })}
                        className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  )}
                </div>

                {/* Image sections */}
                {[
                  { key: "images", label: "Main Images", color: "bg-purple-600 hover:bg-purple-700", uploading: uploadingImages },
                  { key: "amenityImages", label: "Amenity Images", color: "bg-purple-600 hover:bg-purple-700", uploading: uploadingAmenityImages },
                  { key: "eventShowcase", label: "Event Showcase", color: "bg-purple-600 hover:bg-purple-700", uploading: uploadingEventImages },
                ].map(({ key, label, color, uploading }) => (
                  <div key={key}>
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="font-['Manrope'] text-base font-bold text-gray-900">{label}</h3>
                      <label className={`flex items-center space-x-2 text-white px-4 py-2 rounded-lg cursor-pointer transition-colors ${color}`}>
                        {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />}
                        <span>Upload</span>
                        <input type="file" multiple accept="image/*" onChange={(e) => handleImageUpload(e.target.files, key)} className="hidden" />
                      </label>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {(formData[key] || []).map((image, idx) => (
                        <div key={idx} className="relative group">
                          <img src={image.url} alt="" className="w-full h-28 object-cover rounded-xl border border-gray-200/70" />
                          <button
                            onClick={() => setFormData({ ...formData, [key]: formData[key].filter((_, i) => i !== idx) })}
                            className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}

                {/* Videos */}
                <div>
                  <h3 className="font-['Manrope'] text-base font-bold text-gray-900 mb-3">Videos</h3>
                  <input
                    type="text"
                    placeholder="Paste video URL and press Enter"
                    className="w-full px-4 py-2.5 text-sm text-gray-900 placeholder:text-gray-400 border border-gray-200 rounded-xl hover:border-gray-300 focus:outline-none focus:ring-4 focus:ring-purple-500/10 focus:border-purple-400 transition-all"
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && e.target.value) {
                        setFormData({ ...formData, videos: [...formData.videos, { url: e.target.value, caption: "" }] });
                        e.target.value = "";
                      }
                    }}
                  />
                  {formData.videos?.map((video, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg mt-2">
                      <span className="text-sm text-gray-700 truncate">{video.url}</span>
                      <button
                        onClick={() => setFormData({ ...formData, videos: formData.videos.filter((_, i) => i !== idx) })}
                        className="text-red-500 hover:text-red-700 ml-2"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* ── DETAILS ───────────────────────────────────────────────── */}
            {activeTab === "details" && (
              <div className="space-y-6">
                <ServiceAIAssistant
                  serviceType={formData.serviceType || "venue"}
                  step="amenities"
                  formData={formData}
                  onApply={({ amenity }) => {
                    if (amenity && !formData.amenities?.includes(amenity)) {
                      setFormData({ ...formData, amenities: [...(formData.amenities || []), amenity] });
                    }
                  }}
                  compact={false}
                />

                {/* Areas Available */}
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-gradient-to-r from-purple-100 to-pink-100 rounded-xl flex items-center justify-center">
                        <Users className="w-5 h-5 text-purple-600" />
                      </div>
                      <div>
                        <h3 className="text-lg font-bold text-gray-900">Areas Available</h3>
                        <p className="text-xs text-gray-500">
                          {formData.areas_available?.length || 0} area{formData.areas_available?.length !== 1 ? "s" : ""} added
                        </p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={addArea}
                      className="flex items-center space-x-2 bg-gradient-to-r from-purple-500 to-pink-600 text-white px-4 py-2 rounded-xl hover:from-purple-600 hover:to-pink-700 transition-all shadow-md font-medium text-sm"
                    >
                      <Users className="w-4 h-4 text-purple-600" />
                      <span>Add Area</span>
                    </button>
                  </div>

                  <AIKeywordFill
                    serviceType="venue"
                    step="areas"
                    formData={formData}
                    accent="amber"
                    label="Add areas with AI"
                    buttonText="Generate"
                    placeholder="e.g. grand banquet hall, poolside lawn, rooftop terrace"
                    onResult={(res) => {
                      const incoming = (res?.areas || [])
                        .filter((a) => a && (a.title || a.type))
                        .map((a) => ({
                          title: a.title || "",
                          type: a.type === "outdoor" ? "outdoor" : a.type === "indoor" ? "indoor" : "",
                          seating: Number(a.seating) || 0,
                          floating: Number(a.floating) || 0,
                          description: a.description || "",
                          images: [],
                        }));
                      if (incoming.length === 0) return;
                      setFormData((prev) => ({
                        ...prev,
                        areas_available: [...(prev.areas_available || []), ...incoming],
                      }));
                    }}
                  />

                  {formData.areas_available?.length > 0 ? (
                    <div className="space-y-4">
                      {formData.areas_available.map((area, idx) => (
                        <div key={idx} className="group relative border border-gray-200 rounded-2xl p-5 bg-gradient-to-br from-white to-gray-50 hover:shadow-md transition-all">
                          <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center space-x-2">
                              <div className="w-7 h-7 bg-gradient-to-r from-purple-500 to-pink-500 rounded-lg flex items-center justify-center text-white text-xs font-bold">
                                {idx + 1}
                              </div>
                              <span className="font-semibold text-gray-800">Area {idx + 1}</span>
                            </div>
                            <button
                              type="button"
                              onClick={() => removeArea(idx)}
                              className="flex items-center space-x-1 bg-red-50 text-red-600 px-3 py-1.5 rounded-lg hover:bg-red-100 transition-all text-sm font-medium"
                            >
                              <X className="w-3.5 h-3.5" />
                              <span>Remove</span>
                            </button>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <label className="block text-[13px] font-semibold text-gray-700 mb-1.5">Area Name *</label>
                              <input type="text" value={area.title || ""} onChange={(e) => updateArea(idx, "title", e.target.value)}
                                className="w-full px-4 py-2.5 text-sm text-gray-900 placeholder:text-gray-400 border border-gray-200 rounded-xl hover:border-gray-300 focus:outline-none focus:ring-4 focus:ring-purple-500/10 focus:border-purple-400 transition-all bg-white shadow-sm"
                                placeholder="Main Hall, Lawn Area, Terrace, etc." maxLength={50} />
                            </div>
                            <div>
                              <label className="block text-[13px] font-semibold text-gray-700 mb-1.5">Type *</label>
                              <select value={area.type || ""} onChange={(e) => updateArea(idx, "type", e.target.value)}
                                className="w-full px-4 py-2.5 text-sm text-gray-900 placeholder:text-gray-400 border border-gray-200 rounded-xl hover:border-gray-300 focus:outline-none focus:ring-4 focus:ring-purple-500/10 focus:border-purple-400 transition-all bg-white shadow-sm appearance-none">
                                <option value="">Select Area Type</option>
                                <option value="indoor">Indoor</option>
                                <option value="outdoor">Outdoor</option>
                              </select>
                            </div>
                            <div>
                              <label className="block text-[13px] font-semibold text-gray-700 mb-1.5">Seating Capacity</label>
                              <input type="number" value={area.seating || 0} onChange={(e) => updateArea(idx, "seating", parseInt(e.target.value) || 0)}
                                className="w-full px-4 py-2.5 text-sm text-gray-900 placeholder:text-gray-400 border border-gray-200 rounded-xl hover:border-gray-300 focus:outline-none focus:ring-4 focus:ring-purple-500/10 focus:border-purple-400 transition-all bg-gradient-to-r from-purple-50 to-pink-50 shadow-sm"
                                placeholder="100" min="0" />
                            </div>
                            <div>
                              <label className="block text-[13px] font-semibold text-gray-700 mb-1.5">Floating Capacity</label>
                              <input type="number" value={area.floating || 0} onChange={(e) => updateArea(idx, "floating", parseInt(e.target.value) || 0)}
                                className="w-full px-4 py-2.5 text-sm text-gray-900 placeholder:text-gray-400 border border-gray-200 rounded-xl hover:border-gray-300 focus:outline-none focus:ring-4 focus:ring-purple-500/10 focus:border-purple-400 transition-all bg-gradient-to-r from-purple-50 to-pink-50 shadow-sm"
                                placeholder="150" min="0" />
                            </div>
                            <div className="md:col-span-2">
                              <label className="block text-[13px] font-semibold text-gray-700 mb-1.5">Description</label>
                              <textarea value={area.description || ""} onChange={(e) => updateArea(idx, "description", e.target.value)}
                                rows={3} className="w-full px-4 py-2.5 text-sm text-gray-900 placeholder:text-gray-400 border border-gray-200 rounded-xl hover:border-gray-300 focus:outline-none focus:ring-4 focus:ring-purple-500/10 focus:border-purple-400 transition-all resize-vertical shadow-sm"
                                placeholder="Describe this area..." maxLength={300} />
                              <p className="text-xs text-gray-400 mt-1 text-right">{area.description?.length || 0}/300</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="flex flex-col items-center justify-center text-center py-6 border-2 border-dashed border-gray-300 rounded-xl bg-gradient-to-b from-gray-50 to-pink-50">
                      <h4 className="text-lg font-bold text-gray-900 mb-2">No Areas Added Yet</h4>
                      <button type="button" onClick={addArea}
                        className="flex items-center space-x-2 bg-gradient-to-r from-purple-500 to-pink-600 text-white px-4 py-2 rounded-xl hover:from-purple-600 hover:to-pink-700 transition-all shadow-md font-medium">
                        <Users className="w-4 h-4" />
                        <span>Add Areas</span>
                      </button>
                    </div>
                  )}
                </div>

                {/* Venue Type */}
                <div>
                  <label className="block text-[13px] font-semibold text-gray-700 mb-1.5">
                    <Building className="inline w-4 h-4 mr-1" />
                    Venue Type
                  </label>
                  <div className="grid grid-cols-3 gap-3">
                    {[
                      { value: "indoor", label: "Indoor" },
                      { value: "outdoor", label: "Outdoor" },
                      { value: "mixed", label: "Both / Mixed" },
                    ].map((vt) => (
                      <button
                        key={vt.value}
                        type="button"
                        onClick={() => setFormData({ ...formData, venueType: vt.value })}
                        className={`py-3 px-4 rounded-lg border-2 text-sm font-medium transition-all ${
                          formData.venueType === vt.value
                            ? "border-purple-500 bg-purple-50 text-purple-700"
                            : "border-gray-200 bg-white text-gray-600 hover:border-purple-300"
                        }`}
                      >
                        {vt.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Catering & Cuisine — driven by the External Vendor Policy
                    (externalVendors.catering) from the service model. Shown when
                    catering is offered; the in-house menu picker only when the
                    venue itself caters (in_house_only). */}
                {formData.externalVendors?.catering !== "not_allowed" && (
                  <div className="border border-purple-100 bg-purple-50/40 rounded-xl p-4">
                    <label className="block text-sm font-semibold text-gray-800 mb-1">
                      <Utensils className="inline w-4 h-4 mr-1 text-purple-600" />
                      Catering & Cuisine
                    </label>
                    <p className="text-xs text-gray-500 mb-3">
                      Shown because your External Vendor Policy allows catering
                      {formData.externalVendors?.catering === "in_house_only" ? " (in-house only)" : " (outside caterers permitted)"}.
                    </p>

                    <AIKeywordFill
                      serviceType="venue"
                      step="cuisine"
                      formData={formData}
                      accent="green"
                      label="Set veg / non-veg & cuisines with AI"
                      buttonText="Apply"
                      placeholder="e.g. pure veg, jain food, north indian, chinese, non-veg allowed"
                      onResult={(res) => {
                        setFormData((prev) => {
                          const incoming = Array.isArray(res?.typeOfCuisine) ? res.typeOfCuisine : [];
                          const mergedMenu = Array.from(
                            new Set([...(prev.cateringMenu || []), ...incoming.filter(Boolean)])
                          );
                          return {
                            ...prev,
                            cuisineAllowed: {
                              veg: typeof res?.veg === "boolean" ? res.veg : prev.cuisineAllowed?.veg || false,
                              nonVeg: typeof res?.nonVeg === "boolean" ? res.nonVeg : prev.cuisineAllowed?.nonVeg || false,
                            },
                            cateringMenu: mergedMenu,
                          };
                        });
                      }}
                    />

                    {/* Veg / Non-Veg */}
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Food Type</p>
                    <div className="flex space-x-4 mb-4">
                      {[{ k: "veg", l: "Vegetarian" }, { k: "nonVeg", l: "Non-Vegetarian" }].map((c) => (
                        <label key={c.k} className="flex items-center space-x-2">
                          <input type="checkbox" checked={formData.cuisineAllowed[c.k]}
                            onChange={(e) => setFormData({ ...formData, cuisineAllowed: { ...formData.cuisineAllowed, [c.k]: e.target.checked } })}
                            className="w-4 h-4  border-gray-300 rounded accent-purple-600 cursor-pointer" />
                          <span className="text-sm">{c.l}</span>
                        </label>
                      ))}
                    </div>

                    {/* In-house catering menu tags — only when the venue itself
                        caters (External Vendor Policy = in_house_only). */}
                    {formData.externalVendors?.catering === "in_house_only" && (
                    <>
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                      Cuisines &amp; menu offered
                      {formData.cateringMenu?.length > 0 && (
                        <span className="ml-1 text-purple-600 normal-case font-medium">({formData.cateringMenu.length} selected)</span>
                      )}
                    </p>
                    <div className="space-y-2 max-h-52 overflow-y-auto pr-1">
                      {Object.entries(includeGroups("caterers"))
                        .filter(([cat]) => /cuisine|counter|beverage|dessert|dietary/i.test(cat))
                        .map(([cat, tags]) => (
                          <div key={cat}>
                            <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-1">{cat}</p>
                            <div className="flex flex-wrap gap-1.5">
                              {tags.map((tag) => {
                                const selected = (formData.cateringMenu || []).includes(tag);
                                return (
                                  <button
                                    key={tag}
                                    type="button"
                                    onClick={() =>
                                      setFormData((prev) => ({
                                        ...prev,
                                        cateringMenu: selected
                                          ? prev.cateringMenu.filter((t) => t !== tag)
                                          : [...(prev.cateringMenu || []), tag],
                                      }))
                                    }
                                    className={`text-[11px] px-2 py-0.5 rounded-full border transition ${
                                      selected
                                        ? "bg-gradient-to-r from-purple-600 to-pink-600 border-purple-500 text-white"
                                        : "bg-white border-gray-200 text-gray-600 hover:border-purple-300"
                                    }`}
                                  >
                                    {selected ? <Check className="mr-1 inline h-3 w-3 align-[-1px]" /> : <Plus className="mr-1 inline h-3 w-3 align-[-1px]" />}{tag}
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        ))}
                    </div>
                    </>
                    )}
                  </div>
                )}

                {/* Time Slots */}
                <div>
                  <h3 className="font-['Manrope'] text-base font-bold text-gray-900 mb-3">
                    <Clock className="inline w-5 h-5 mr-2" />
                    Available Time Slots
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {formData.venueSlots.map((slot, idx) => (
                      <label key={idx} className="flex items-center space-x-2 p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                        <input type="checkbox" checked={slot.available}
                          onChange={(e) => {
                            const newSlots = [...formData.venueSlots];
                            newSlots[idx].available = e.target.checked;
                            setFormData({ ...formData, venueSlots: newSlots });
                          }}
                          className="w-4 h-4 accent-purple-600 cursor-pointer" />
                        <span className="text-sm font-medium capitalize">{slot.slot.replace("-", " ")}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Amenities */}
                <div>
                  <h3 className="font-['Manrope'] text-base font-bold text-gray-900 mb-3">Amenities</h3>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {/* Render the base options plus any already-selected amenities
                        that aren't in the base list (e.g. AI-added), so every
                        selected amenity is visible and removable. */}
                    {Array.from(new Set([...amenitiesOptions, ...(formData.amenities || [])])).map((amenity) => (
                      <label key={amenity} className="flex items-center space-x-2">
                        <input type="checkbox" checked={formData.amenities?.includes(amenity) || false}
                          onChange={(e) => {
                            const newAmenities = e.target.checked
                              ? [...(formData.amenities || []), amenity]
                              : (formData.amenities || []).filter((a) => a !== amenity);
                            setFormData({ ...formData, amenities: newAmenities });
                          }}
                          className="w-4 h-4 accent-purple-600 cursor-pointer" />
                        <span className="text-sm">{amenity.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Parking */}
                <div className="grid grid-cols-2 gap-4">
                  <label className="flex items-center space-x-2">
                    <input type="checkbox" checked={formData.parking.available}
                      onChange={(e) => setFormData({ ...formData, parking: { ...formData.parking, available: e.target.checked } })}
                      className="w-4 h-4 accent-purple-600 cursor-pointer" />
                    <Car className="w-4 h-4" />
                    <span className="text-sm font-medium">Parking Available</span>
                  </label>
                  {formData.parking.available && (
                    <input type="number" value={formData.parking.capacity}
                      onChange={(e) => setFormData({ ...formData, parking: { ...formData.parking, capacity: parseInt(e.target.value) || 0 } })}
                      placeholder="Parking capacity" className="px-4 py-2 border rounded-lg" min="0" />
                  )}
                </div>

                {/* Accessibility */}
                <div>
                  <h3 className="font-['Manrope'] text-base font-bold text-gray-900 mb-3">
                    <Shield className="inline w-5 h-5 mr-2" />
                    Accessibility Features
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {accessibilityOptions.map((feature) => (
                      <label key={feature} className="flex items-center space-x-2">
                        <input type="checkbox" checked={formData.accessibilityFeatures?.includes(feature) || false}
                          onChange={(e) => {
                            const newFeatures = e.target.checked
                              ? [...(formData.accessibilityFeatures || []), feature]
                              : (formData.accessibilityFeatures || []).filter((f) => f !== feature);
                            setFormData({ ...formData, accessibilityFeatures: newFeatures });
                          }}
                          className="w-4 h-4 accent-purple-600 cursor-pointer" />
                        <span className="text-sm">{feature.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* ── PRICING ───────────────────────────────────────────────── */}
            {activeTab === "pricing" && (
              <div className="space-y-6">
                <ServiceAIAssistant
                  serviceType={formData.serviceType || "venue"}
                  step="pricing"
                  formData={formData}
                  compact={false}
                />
                <div>
                  <label className="block text-[13px] font-semibold text-gray-700 mb-1.5">Pricing Model *</label>
                  <select value={formData.pricing.model}
                    onChange={(e) => setFormData({ ...formData, pricing: { ...formData.pricing, model: e.target.value } })}
                    className="w-full px-4 py-2.5 text-sm text-gray-900 placeholder:text-gray-400 border border-gray-200 rounded-xl hover:border-gray-300 focus:outline-none focus:ring-4 focus:ring-purple-500/10 focus:border-purple-400 transition-all bg-white">
                    <option value="per_pax">Price Per Plate</option>
                    <option value="fixed_booking">Venue Rent</option>
                    <option value="mixed">Multi Pricing</option>
                    <option value="package_per_day">Package Per Day</option>
                  </select>
                </div>
                {(formData.pricing.model === "per_pax" || formData.pricing.model === "mixed") && (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-[13px] font-semibold text-gray-700 mb-1.5">Veg Price (₹/plate)</label>
                      <input type="number" value={formData.pricing.perPax.veg}
                        onChange={(e) => setFormData({ ...formData, pricing: { ...formData.pricing, perPax: { ...formData.pricing.perPax, veg: parseInt(e.target.value) || 0 } } })}
                        className="w-full px-4 py-2.5 text-sm text-gray-900 placeholder:text-gray-400 border border-gray-200 rounded-xl hover:border-gray-300 focus:outline-none focus:ring-4 focus:ring-purple-500/10 focus:border-purple-400 transition-all bg-white" min="0" />
                    </div>
                    <div>
                      <label className="block text-[13px] font-semibold text-gray-700 mb-1.5">Non-Veg Price (₹/plate)</label>
                      <input type="number" value={formData.pricing.perPax.nonVeg}
                        onChange={(e) => setFormData({ ...formData, pricing: { ...formData.pricing, perPax: { ...formData.pricing.perPax, nonVeg: parseInt(e.target.value) || 0 } } })}
                        className="w-full px-4 py-2.5 text-sm text-gray-900 placeholder:text-gray-400 border border-gray-200 rounded-xl hover:border-gray-300 focus:outline-none focus:ring-4 focus:ring-purple-500/10 focus:border-purple-400 transition-all bg-white" min="0" />
                    </div>
                  </div>
                )}
                {(formData.pricing.model === "fixed_booking" || formData.pricing.model === "mixed") && (
                  <div>
                    <label className="block text-[13px] font-semibold text-gray-700 mb-1.5">Venue Rent (₹)</label>
                    <input type="number" value={formData.pricing.fixedBooking.basePrice}
                      onChange={(e) => setFormData({ ...formData, pricing: { ...formData.pricing, fixedBooking: { ...formData.pricing.fixedBooking, basePrice: parseInt(e.target.value) || 0 } } })}
                      className="w-full px-4 py-2.5 text-sm text-gray-900 placeholder:text-gray-400 border border-gray-200 rounded-xl hover:border-gray-300 focus:outline-none focus:ring-4 focus:ring-purple-500/10 focus:border-purple-400 transition-all bg-white" min="0" />
                    <textarea value={formData.pricing.fixedBooking.description}
                      onChange={(e) => setFormData({ ...formData, pricing: { ...formData.pricing, fixedBooking: { ...formData.pricing.fixedBooking, description: e.target.value } } })}
                      placeholder="Describe what's included..." rows={2} className="w-full px-4 py-3 border rounded-lg mt-2" />
                  </div>
                )}
                {(formData.pricing.model === "package_per_day" || formData.pricing.model === "mixed") && (
                  <div className="grid grid-cols-2 gap-4">
                    {[
                      { key: "price", label: "Package Price (₹)" },
                      { key: "noOfRooms", label: "Number of Rooms" },
                      { key: "noOfPax", label: "Number of Pax" },
                      { key: "noOfMeals", label: "Number of Meals" },
                    ].map(({ key, label }) => (
                      <div key={key}>
                        <label className="block text-[13px] font-semibold text-gray-700 mb-1.5">{label}</label>
                        <input type="number" value={formData.pricing.packagePerDay[key]}
                          onChange={(e) => setFormData({ ...formData, pricing: { ...formData.pricing, packagePerDay: { ...formData.pricing.packagePerDay, [key]: parseInt(e.target.value) || 0 } } })}
                          className="w-full px-4 py-2.5 text-sm text-gray-900 placeholder:text-gray-400 border border-gray-200 rounded-xl hover:border-gray-300 focus:outline-none focus:ring-4 focus:ring-purple-500/10 focus:border-purple-400 transition-all bg-white" min="0" />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* ── PACKAGES ──────────────────────────────────────────────── */}
            {activeTab === "packages" && (
              <div className="space-y-4">
                {service?._id ? (
                  <AIPackageBuilder
                    serviceType={service.serviceType || formData.serviceType || "venue"}
                    serviceData={service}
                    serviceId={service._id}
                  />
                ) : (
                  <div className="border-2 border-dashed border-gray-200 rounded-2xl p-10 text-center">
                    <p className="text-sm font-medium text-gray-600">Save the service first</p>
                    <p className="text-xs text-gray-400 mt-1">
                      Fill the basic details and save, then reopen to build & edit packages here.
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* ── DOCUMENTS ─────────────────────────────────────────────── */}
            {activeTab === "documents" && (
              <div className="space-y-6">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="font-['Manrope'] text-base font-bold text-gray-900 flex items-center space-x-2">
                    <FileText className="w-6 h-6 text-purple-600" />
                    <span>Business Documents</span>
                  </h3>
                  <p className="text-sm text-gray-500">Upload required licenses (PDF/JPG/PNG)</p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {[
                    { key: "businessLicense", label: "Business License", icon: FileCheck },
                    { key: "gstCertificate", label: "GST Certificate", icon: FileCheck },
                    { key: "identityProof", label: "Identity Proof (Aadhar/PAN)", icon: UserCheck },
                    { key: "bankProof", label: "Bank Proof", icon: Banknote },
                    { key: "fssaiCertificate", label: "FSSAI Certificate", icon: Shield },
                    { key: "liquorLicense", label: "Liquor License", icon: Shield },
                  ].map(({ key, label, icon: Icon }) => (
                    <div key={key} className="rounded-2xl border border-gray-200/70 bg-white p-4 hover:border-purple-200 hover:shadow-md hover:shadow-purple-900/5 transition-all">
                      <label className="text-[13px] font-semibold text-gray-700 mb-1.5 flex items-center space-x-2">
                        <Icon className="w-4 h-4 text-purple-500" />
                        <span>{label}</span>
                      </label>
                      <input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => handleBusinessDocUpload(e.target.files, key)} className="hidden" id={`doc-${key}`} />
                      <label htmlFor={`doc-${key}`} className="w-full flex items-center justify-center p-4 border-2 border-dashed border-gray-200 rounded-xl hover:border-purple-300 hover:bg-purple-50/50 transition-all cursor-pointer">
                        {uploadingBusinessDocs ? (
                          <Loader2 className="w-5 h-5 animate-spin text-purple-600" />
                        ) : (
                          <>
                            <Upload className="w-5 h-5 text-gray-400 mr-2" />
                            <span className="text-sm text-gray-600">Click to upload</span>
                          </>
                        )}
                      </label>
                      {formData.businessDocuments[key] && (
                        <div className="mt-3 p-3 bg-purple-50 border border-purple-200 rounded-lg flex items-center justify-between">
                          <span className="text-sm text-purple-800 flex items-center space-x-2 truncate">
                            <CheckCircle className="w-4 h-4 flex-shrink-0" />
                            <span className="truncate">{formData.businessDocuments[key].name}</span>
                          </span>
                          <button onClick={() => setFormData({ ...formData, businessDocuments: { ...formData.businessDocuments, [key]: null } })}
                            className="text-red-600 hover:text-red-800 ml-2 flex-shrink-0">
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* ── PAYOUT ────────────────────────────────────────────────── */}
            {activeTab === "payout" && (
              <div>
                <h3 className="font-['Manrope'] text-base font-bold text-gray-900 mb-4 flex items-center space-x-2">
                  <CreditCard className="w-6 h-6 text-purple-600" />
                  <span>Payout Bank Details</span>
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {[
                    { key: "accountHolderName", label: "Account Holder Name", ph: "John Doe" },
                    { key: "accountNumber", label: "Account Number", ph: "123456789012" },
                    { key: "ifscCode", label: "IFSC Code", ph: "SBIN0001234", upper: true },
                    { key: "bankName", label: "Bank Name", ph: "State Bank of India" },
                  ].map((f) => (
                    <div key={f.key}>
                      <label className="block text-[13px] font-semibold text-gray-700 mb-1.5">{f.label}</label>
                      <input type="text" value={formData.payoutBankDetails[f.key]}
                        onChange={(e) => setFormData({ ...formData, payoutBankDetails: { ...formData.payoutBankDetails, [f.key]: f.upper ? e.target.value.toUpperCase() : e.target.value } })}
                        className="w-full px-4 py-2.5 text-sm text-gray-900 placeholder:text-gray-400 border border-gray-200 rounded-xl hover:border-gray-300 focus:outline-none focus:ring-4 focus:ring-purple-500/10 focus:border-purple-400 transition-all"
                        placeholder={f.ph} />
                    </div>
                  ))}
                  <div className="md:col-span-2">
                    <label className="block text-[13px] font-semibold text-gray-700 mb-1.5">Branch Name</label>
                    <input type="text" value={formData.payoutBankDetails.branchName}
                      onChange={(e) => setFormData({ ...formData, payoutBankDetails: { ...formData.payoutBankDetails, branchName: e.target.value } })}
                      className="w-full px-4 py-2.5 text-sm text-gray-900 placeholder:text-gray-400 border border-gray-200 rounded-xl hover:border-gray-300 focus:outline-none focus:ring-4 focus:ring-purple-500/10 focus:border-purple-400 transition-all"
                      placeholder="MG Road Branch" />
                  </div>
                </div>
              </div>
            )}

            {/* ── POLICIES ──────────────────────────────────────────────── */}
            {activeTab === "policies" && (
              <div className="space-y-6">
                <label className="flex items-center space-x-2">
                  <input type="checkbox" checked={formData.smokingAllowed}
                    onChange={(e) => setFormData({ ...formData, smokingAllowed: e.target.checked })}
                    className="w-4 h-4 text-red-600 accent-purple-600 cursor-pointer" />
                  <Cigarette className="w-4 h-4" />
                  <span className="text-sm font-medium">Smoking Allowed</span>
                </label>
                <div>
                  <h3 className="font-['Manrope'] text-base font-bold text-gray-900 mb-3">External Vendor Policies</h3>
                  <div className="space-y-4">
                    {Object.keys(formData.externalVendors).map((vendor) => (
                      <div key={vendor}>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5 capitalize">{vendor}</label>
                        <select value={formData.externalVendors[vendor]}
                          onChange={(e) => setFormData({ ...formData, externalVendors: { ...formData.externalVendors, [vendor]: e.target.value } })}
                          className="w-full px-4 py-2 border rounded-lg">
                          <option value="not_allowed">Not Allowed</option>
                          <option value="in_house_only">In-House Only</option>
                          <option value="outside_permitted">Outside Permitted</option>
                        </select>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>

          <ServiceFormFooter
            tabs={VENUE_TABS}
            activeTab={activeTab}
            onTabChange={setActiveTab}
            isLive={formData.isLive}
            onLiveChange={(isLive) => setFormData({ ...formData, isLive })}
            isGst={formData.isGst}
            onGstChange={(isGst) => setFormData({ ...formData, isGst })}
            onClose={onClose}
            onSave={handleSave}
            loading={loading}
            isEdit={!!service}
          />
      </div>
    </div>
  );
};

export default ServiceModal;

